/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useRef, useState} from "react";
import {BookingSteps} from "types/enums/BookingSteps";
import {useDispatch} from "useDispatch";
import BookingAction from "store/booking/BookingAction";
import {useTypedSelector} from "helpers/reducers";
import Popup from "../components/Popup";
import {
    DEFAULT_HALL_ID,
    ENABLED_COUNTDOWN_TIMER,
    GOOGLE_ANALYTICS_KEY,
    GOOGLE_TAG_MANAGER_ID,
    IS_DEV
} from "helpers/config";
import Cookies from "components/Cookies/Cookies";
import TagManager from "react-gtm-module";
import {RouteComponentProps, useLocation, withRouter} from "react-router";
import SessionAction from "store/session/SessionAction";
import HallAction from "store/hall/HallAction";
import {ThunkAction} from "redux-thunk";
import ReactGA from "react-ga4";
import history from "helpers/history";
import {PaymentStatus} from "types/enums/PaymentStatus";
import VoucherAction from "store/shop/VoucherAction";
import clsx from "clsx";
import {useNavigationSteps, useQueryParams} from "utils/bookingUtils";
import BookingPages from "pages/Front/Booking/Pages/BookingPages";
import {useCookies} from "utils/hooks/useCookies";
import BookingFooterNew from "pages/Front/Booking/BookingFooterNew";
import useNavigation from "utils/hooks/useNavigation";
import CountdownTimer from "pages/Front/Booking/components/CountdownTimer";

interface BookingLayoutSellerProps extends RouteComponentProps {}

const BookingLayoutSeller = (props: BookingLayoutSellerProps) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const {getCurrentPage} = useNavigation();
    const bookingSteps = useNavigationSteps();
    const queryParams = useQueryParams();

    const hall = useTypedSelector(state => state.hallState.hall);
    const bookingState = useTypedSelector(state => state.bookingState);
    const voucherItems = useTypedSelector(state => state.voucherState.voucherItems);
    const packagesCart = bookingState.bookingProcess.packageCart;
    const bookingFilter = bookingState.bookingProcess.filter;
    const bookingPackage = useTypedSelector(state => state.bookingState.bookingProcess.package);
    const session = useTypedSelector(state => state.sessionState.session);
    const [bookingStep, setBookingStep] = useState<number>(BookingSteps.Intro);
    const [voucherItemsCount, setVoucherItemsCount] = useState(voucherItems.length);
    const loadingRef = useRef(false);
    const {cookiesEnabled, showCookiesBanner} = useCookies();

    const cartTagManagerArgs = {
        dataLayer: {
            bookingState: bookingState,
            pageProject: "Booking" + (hall ? hall.name : ""),
        },
        dataLayerName: "BookingPages",
    };

    const pageInit = () => {
        if (!session) {
            dispatch(SessionAction.loginVisitor());
        }
        dispatch(HallAction.fetchHall(DEFAULT_HALL_ID)).then(r => {});
        dispatch<ThunkAction<any, any, any, any>>(BookingAction.fetchActivities());
    };

    const pageCleanup = () => {
        if (!location.pathname.includes("booking/paid")) {
            if (packagesCart?.payment?.status === PaymentStatus.Finished) {
                dispatch(VoucherAction.resetVouchers());
                dispatch<ThunkAction<any, any, any, any>>(BookingAction.initCart()).then(r => {
                    dispatch(BookingAction.clearBookingProcess());
                });
            }
        }
    };

    const pageRender = () => {
        window.scrollTo(0, 0);
        bookingSteps.updateSteps(getCurrentPage());
    };

    const selectTimeslotHandler = (bookingStep: BookingSteps) => {
        setBookingStep(bookingStep);
    };

    // const setSelectedBookingFilterHandler = () => {
    //     consoleLog("setSelectedBookingFilterHandler");
    //     dispatch(
    //         openModal(
    //             <BookingFilterMinimal filterMinimal={true} package={bookingPackage} proceedToUpsell={false}>
    //                 <PackageRequirements package={bookingPackage} />
    //             </BookingFilterMinimal>,
    //             {}
    //         )
    //     );
    // };

    const googleAnalytics = () => {
        // if (IS_PROD) {
        ReactGA.initialize(GOOGLE_ANALYTICS_KEY);
        ReactGA.send({hitType: "pageview", page: window.location.pathname}); // Record a pageview for the given page

        history.listen(location => {
            //console.log(location);
            ReactGA.set({page: window.location.pathname}); // Update the user's current page
            ReactGA.send({hitType: "pageview", page: window.location.pathname}); // Record a pageview for the given page
        });
        // }
    };

    const googleTagManager = () => {
        TagManager.initialize({
            gtmPreview: true,
            gtmId: GOOGLE_TAG_MANAGER_ID,
        });
    };

    useEffect(() => {
        if (cookiesEnabled && !IS_DEV) {
            googleAnalytics();
            googleTagManager();
        }
    }, [cookiesEnabled]);

    useEffect(() => {
        if (TagManager && !IS_DEV) {
            TagManager.dataLayer(cartTagManagerArgs);
        }
    }, []);

    useEffect(() => {
        pageInit();
    }, []);

    useEffect(() => {
        pageCleanup();
    }, [packagesCart]);

    useEffect(() => {
        pageRender();
    }, [location.pathname]);

    useEffect(() => {
        queryParams.fetchPackagesForHall();
    }, [bookingFilter.date]);

    useEffect(() => {
        setVoucherItemsCount(voucherItems.length);
    }, [voucherItems]);

    return (
        <div className={"BookingLayoutSeller h-full flex flex-col flex-grow m-auto justify-between w-full"}>
            <div className={"relative"}>
                <div
                    // className={clsx("Booking max-w-full xl:max-w-7xl w-full items-center m-auto flex flex-col grow pt-6 pb-6 px-4 xl:px-0")}
                    className={clsx("Booking max-w-full w-full items-center m-auto flex flex-col grow pt-6 pb-6 px-4")}
                    role="document"
                >
                    {ENABLED_COUNTDOWN_TIMER && (location.pathname === "/booking/customer" || location.pathname.includes("/booking/book/")) && (
                        <div className={"mt-0 mb-2 sm:mb-0 lg:my-2 grow justify-center sm:justify-end items-end flex flex-row lg:self-start"}>
                            <CountdownTimer />
                        </div>
                    )}
                    {ENABLED_COUNTDOWN_TIMER && location.pathname.includes("/booking/timeslots") && (
                        <div className={"mb-4 justify-center items-center flex flex-row"}>
                            <CountdownTimer />
                        </div>
                    )}
                    <div className={"BookingPages overflow-visible grow flex flex-col items-start w-full"}>
                        <BookingPages selectTimeslotHandler={selectTimeslotHandler} />
                    </div>
                </div>
            </div>

            {showCookiesBanner && <Cookies />}
            <Popup />

            <BookingFooterNew isFullWidth={true} />
        </div>
    );
};

export default withRouter(BookingLayoutSeller);
